import { ArrowBigRightIcon } from "lucide-react";
import type React from "react";
import co2CalculatorImg from "../../assets/co2-calculator.png";
import logoImg from "../../assets/logo.png";
import { useI18n } from "../../lib/i18n";
import { LanguageSelector } from "./LanguageSelector";

/**
 * Props for IntroductionPage component
 */
interface IntroductionPageProps {
  onNavigateToCalculator: () => void;
}

/**
 * Introduction page presenting Transmoove and the R scale
 * Faithfully reproduces the Excel file design
 */
const IntroductionPage: React.FC<IntroductionPageProps> = ({
  onNavigateToCalculator,
}) => {
  const { t, isLoaded } = useI18n();

  // Loading display if translations are not yet loaded
  if (!isLoaded) {
    return (
      <div className="tm-page flex items-center justify-center">
        <p className="text-transmoove-text text-sm">Loading...</p>
      </div>
    );
  }

  return (
    <div className="tm-page">
      <div className="px-6 py-4">
        <div className="relative">
          <div className="absolute top-0 right-0">
            <LanguageSelector />
          </div>

          <div className="grid gap-3 lg:grid-cols-[240px_1fr_1fr] items-start">
            <div>
              <img src={logoImg} alt="Transmoove" className="h-12 w-auto" />
            </div>

            <div className="flex justify-center">
              <div className="flex flex-col items-center">
                <img
                  src={co2CalculatorImg}
                  alt="CO2 calculator"
                  className="h-16 w-16"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-[260px_320px_1fr] items-start">
          <div className="text-tm-base font-semibold leading-relaxed text-transmoove-text">
            <p>{t("intro.description.long.p1")}</p>
            <p className="mt-2">{t("intro.description.long.p2")}</p>
            <p className="mt-2">{t("intro.description.long.p3")}</p>
          </div>

          <div className="text-tm-base leading-relaxed text-transmoove-text">
            <div className="font-semibold mb-3">{t("intro.scaleR.title")}</div>
            <p>{t("intro.scaleR.description.p1")}</p>
            <p className="mt-2">{t("intro.scaleR.description.p2")}</p>
          </div>

          <div>
            <div className="tm-bar px-4 py-2 text-tm-md font-semibold text-center mr-auto w-1/2">
              {t("intro.scenarios.title")}
            </div>

            <div className="border-y border-transmoove-bar tm-table-r-bg mt-4">
              <div className="grid grid-cols-[140px_1fr] border-b border-transmoove-bar">
                <div className="p-4 text-tm-md font-semibold text-transmoove-text text-center">
                  {t("intro.scenario.refuse.name")}
                </div>
                <div className="p-4 text-tm-base text-transmoove-text">
                  {t("intro.scenario.refuse.desc")}
                </div>
              </div>
              <div className="grid grid-cols-[140px_1fr] border-b border-transmoove-bar">
                <div className="p-4 text-tm-md font-semibold text-transmoove-text text-center">
                  {t("intro.scenario.moveReuse.name")}
                </div>
                <div className="p-4 text-tm-base text-transmoove-text">
                  {t("intro.scenario.moveReuse.desc")}
                </div>
              </div>
              <div className="grid grid-cols-[140px_1fr] border-b border-transmoove-bar">
                <div className="p-4 text-tm-md font-semibold text-transmoove-text text-center">
                  {t("intro.scenario.reuseRefurbish.name")}
                </div>
                <div className="p-4 text-tm-base text-transmoove-text">
                  {t("intro.scenario.reuseRefurbish.desc")}
                </div>
              </div>
              <div className="grid grid-cols-[140px_1fr]">
                <div className="p-4 text-tm-md font-semibold text-transmoove-text text-center">
                  {t("intro.scenario.new.name")}
                </div>
                <div className="p-4 text-tm-base text-transmoove-text">
                  {t("intro.scenario.new.desc")}
                </div>
              </div>
            </div>

            <div className="mt-8 tm-bar px-4 py-2 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={onNavigateToCalculator}
                className="text-tm-md font-semibold"
              >
                <ArrowBigRightIcon className="inline mr-2" />
                {t("intro.cta")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntroductionPage;
